import React from 'react';
import { ArrowLeft, MoreVertical, User, Printer } from 'lucide-react';
import { useSaleDetail } from './hooks/useSaleDetail';
import StatusBadge from './components/StatusBadge';
import { SALE_STATUSES } from './constants/saleStatuses';
import Timeline from './components/Timeline';
import ClientSection from './components/ClientSection';
import ProductsList from './components/ProductsList';
import PrintOptions from './components/PrintOptions';
import ActionButtons from './components/ActionButtons';
import DeliveryStatusModal from '../components/DeliveryStatusModal';
import PaymentModal from './components/PaymentModal';
import PaymentFlag from './components/PaymentFlag';
import DeliveryFlag from './components/DeliveryFlag';
import SellerFlag from './components/SellerFlag';
import { PrintTicketButton } from '../components/PrintTicketButton';

const SaleDetail = () => {
  const {
    sale,
    showPrintOptions,
    setShowPrintOptions,
    showDeliveryModal,
    setShowDeliveryModal,
    showPaymentModal,
    setShowPaymentModal,
    handleBack,
    handlePrint,
    handleEdit,
    handleReturn,
    handleDelete,
    handleDeliveryClick,
    handleDeliveryConfirm,
    handlePaymentClick,
    handlePaymentConfirm
  } = useSaleDetail();

  // Si no hay venta o está cargando, mostramos un mensaje
  if (!sale) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-gray-500 dark:text-gray-400">No se encontró la venta</div>
      </div>
    );
  }

  // Asegurarnos de que los objetos necesarios existan
  const items = sale.items || [];
  const totals = {
    subtotal: items.reduce((sum, item) => sum + item.subtotal, 0),
    deposits: items.reduce((sum, item) => sum + (item.totalDeposit || 0), 0),
    total: items.reduce((sum, item) => sum + item.subtotal + (item.totalDeposit || 0), 0)
  };
  const history = sale.history || [];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24">
      {/* Header */}
      <div className={`sticky top-0 ${SALE_STATUSES[sale.status].className} border-b dark:border-gray-700 z-10`}>
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <button 
              onClick={handleBack}
              className="mr-3 text-gray-900 dark:text-white"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <StatusBadge status={sale.status} />
            <div className="relative">
              <button 
                onClick={() => setShowPrintOptions(!showPrintOptions)}
                className="p-2 hover:bg-black/5 dark:hover:bg-white/5 rounded-full text-gray-700 dark:text-gray-300"
              >
                <MoreVertical className="w-5 h-5" />
              </button>
              
              {showPrintOptions && (
                <PrintOptions 
                  status={sale.status}
                  onPrint={handlePrint}
                  onEdit={handleEdit}
                  onReturn={handleReturn}
                  onDelete={handleDelete}
                  onClose={() => setShowPrintOptions(false)}
                  sale={sale}
                />
              )}
            </div>
          </div>

          <div className="flex items-baseline justify-between">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">Venta #{sale.display_id}</div>
            <div className="text-xl font-bold text-gray-900 dark:text-white">
              ${totals.total.toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      {/* Contenido */}
      <div className="p-4 space-y-4">
        <ClientSection client={sale.client} saleId={sale.id} />
        <ProductsList items={items} totals={totals} />

        {/* Flags de estado */}
        <div className="space-y-4">
          <PaymentFlag 
            payment={{
              status: sale.payment_status,
              date: sale.paid_at,
              method: sale.payment_method
            }}
            onClick={sale.payment_status !== 'paid' ? handlePaymentClick : undefined}
          />
          <DeliveryFlag 
            delivery={{
              status: sale.delivery_status,
              delivered_at: sale.delivered_at,
              delivered_by: sale.delivered_by,
              items: items,
              totalItems: items.reduce((sum, item) => sum + item.quantity, 0)
            }}
            onClick={sale.delivery_status !== 'delivered' ? handleDeliveryClick : undefined}
          />
          <SellerFlag 
            seller={{
              displayName: sale.created_by_name || 'Usuario',
              createdAt: sale.created_at
            }} 
          />
        </div>

        {/* Timeline */}
        {history.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border dark:border-gray-700">
            <h2 className="font-medium text-gray-900 dark:text-white mb-3">Historial</h2>
            <Timeline history={history} />
          </div>
        )}

        {/* Info adicional */}
        {sale.createdBy && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border dark:border-gray-700">
            <div className="flex items-start gap-3">
              <User className="w-5 h-5 text-gray-400 dark:text-gray-500" />
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Creada por</div>
                <div className="text-gray-900 dark:text-white">{sale.createdBy.name}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <ActionButtons sale={sale} />

      <DeliveryStatusModal
        isOpen={showDeliveryModal}
        onClose={() => setShowDeliveryModal(false)}
        products={items}
        onDeliveryConfirm={handleDeliveryConfirm}
        sale={sale}
      />

      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onConfirm={handlePaymentConfirm}
        totalAmount={totals.total}
        saleId={sale.id}
      />
    </div>
  );
}

export default SaleDetail; 